/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { apiCall } from 'app/shared/lib/api/api';
import { ProviderService } from 'app/cd/services/provider-service';
import * as CD from 'app/cd/models/essette-api';
import { guid } from 'app/shared/lib/guid';


interface IProviderSummaryModelData {
    provider: CD.Provider,
    location?: CD.ProviderLocation,
    readonly?: boolean;
}

@autoinject()
export class ProviderSummaryCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected provider: CD.Provider | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView })
    protected providerLocationId: string | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    protected locations: CD.ProviderLocation[] = [];

    protected id: guid = guid();


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ProviderService,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async activate(model: IProviderSummaryModelData): Promise<void> {
        if(!model) { return; }

        if(!isNullOrUndefined(model.readonly)) {
            this.readonly = !!model.readonly;
        }

        if(model.provider) {
            this.provider = model.provider;

            if(model.location) {
                this.providerLocationId = model.location.id || null;
            }
            this.reload(this.provider);
        }
    }

    protected detached(): void {
        // clean up
    }


    ////****************** Events ***************************************************////
    public async reload(provider: CD.Provider): Promise<void> {
        if(!provider || !provider.id) { return; }
        this.loadProvider(provider.id);
    }

    ////****************** Functions ************************************************////


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadProvider(providerId: string): Promise<void> {
        const includes: string[] = [
            'facility'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadLocationsByProvider(providerId, undefined, includes);
            this.locations = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}

/**
 * Opens the provider summary modal with the global dialog
 * @param provider The provider information to use
 */
export function openProviderSummary(provider: CD.Provider, readonly: boolean = false): void {
    if(!provider || !provider.id) {
        throw new Error('Provider is missing or invalid');
    }

    const dialogOptions: IDialogOptions = {
        width: '8',
        height: '10',
        helpTopic: 'cd.provider-summary',
        title: {
            key: 'cd:routes.provider.summary.$title',
            replace: { display: provider.$display }
        },
        print: true,
        popout: {
            route: 'cd.provider.summary',
            params: { providerId: provider.id }
        }
    };
    const modelData: IProviderSummaryModelData = {
        provider: provider,
        readonly: readonly
    };

    const modalData: IModalData = {
        componentId: 'app/cd/components/summary/provider/provider-summary',
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}

/**
 * Opens the provider location summary modal with the global dialog
 * @param provider The provider information to use
 */
export function openProviderLocationSummary(location: CD.ProviderLocation, readonly: boolean = false): void {
    if(!location || !location.id) {
        throw new Error('Provider Location is missing or invalid');
    }

    // TODO: consider making openProviderSummary and this function call a shared private function
    // in order to reduce repeated code and shave a few bytes off the bundle
    const dialogOptions: IDialogOptions = {
        width: '8',
        height: '10',
        helpTopic: 'cd.provider-summary',
        title: {
            key: 'cd:routes.provider.summary.$title',
            replace: { display: location.provider?.$display ?? location.$display }
        },
        print: true,
        popout: {
            route: 'cd.provider.summary',
            params: {
                providerId: location.provider?.id,
                locationId: location.id
            } // different than openProviderSummary
        }
    };
    console.log(dialogOptions);
    const modelData: IProviderSummaryModelData = {
        provider: location.provider!,
        location: location, // added to openProviderSummary
        readonly: readonly
    };

    const modalData: IModalData = {
        componentId: 'app/cd/components/summary/provider/provider-summary',
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
